import { Button, Tab, Tabs } from '@ds';
import { MdOutlineClear } from 'react-icons/md';
import { FilterDropdown } from '@ui';
import { Key } from 'react';
import { ORGAN_SELECT_LIST, OCS_CONSOLES_STATUS_LIST } from '@/constants/form.constants';
import { UseFiltersType } from '@/hooks/useFilters';
import { OcsConsoleStatus, TrialProgramsFilterType } from '@/api/apis/resources';
import { useHubsSearchableList } from '@/hooks/useHubsSearchableList';
import { OrganType } from '@/api/apis/case';
import { useTrialProgramsList } from '@/hooks/useTrialProgramsList';
import { FILTERS_NAMES, MaintenanceStatus } from '../config';

export type ConsoleTableFilters = {
  organType?: OrganType[];
  nopHub?: string[];
  ocsConsoleStatus?: OcsConsoleStatus[];
  trialProgramIds?: string[];
  isInMaintenance?: boolean;
  trialProgramsFilterType?: TrialProgramsFilterType;
};

type OcsConsolesFiltersProps = UseFiltersType<ConsoleTableFilters>;

export const OcsConsolesFilters = ({ filters, hasFilters, updateFilters, clearFilters }: OcsConsolesFiltersProps) => {
  const {
    list: hubsList,
    isLoading,
    setSearchValue: onHubSearch,
  } = useHubsSearchableList({ searchBy: ['name', 'code'] });

  const { trialProgramsList } = useTrialProgramsList();

  const checkMaintenanceStatus = () => {
    if (filters.isInMaintenance === undefined) {
      return MaintenanceStatus.All;
    }
    if (filters.isInMaintenance) {
      return MaintenanceStatus.Maintenance;
    }
    return MaintenanceStatus.Operational;
  };

  const onSelectionChangeStatus = (value: Key) => {
    let maintenanceStatus: boolean | undefined;
    switch (value) {
      case MaintenanceStatus.Maintenance:
        maintenanceStatus = true;
        break;
      case MaintenanceStatus.Operational:
        maintenanceStatus = false;
        break;
      case MaintenanceStatus.All:
      default:
        maintenanceStatus = undefined;
    }

    updateFilters(maintenanceStatus, 'isInMaintenance');
  };

  const onTrialFilterChange = (values: string[]) => {
    const filterType = values.length > 0 ? TrialProgramsFilterType.AnyOf : undefined;
    updateFilters(filterType, 'trialProgramsFilterType');
    updateFilters(values, 'trialProgramIds');
  };

  return (
    <div className="flex gap-7 items-center">
      <FilterDropdown
        title={FILTERS_NAMES.organType}
        items={ORGAN_SELECT_LIST}
        selectedKeys={filters.organType ?? []}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values, 'organType')}
      />
      <FilterDropdown
        title={FILTERS_NAMES.nopHub}
        items={hubsList}
        selectedKeys={filters.nopHub ?? []}
        mode="multiple"
        isLoading={isLoading}
        onSearchValue={onHubSearch}
        onSelectedKeysChange={(values) => updateFilters(values, 'nopHub')}
      />
      <FilterDropdown
        title={FILTERS_NAMES.ocsConsoleStatus}
        items={OCS_CONSOLES_STATUS_LIST}
        selectedKeys={filters.ocsConsoleStatus ?? []}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values, 'ocsConsoleStatus')}
      />
      <FilterDropdown
        title={FILTERS_NAMES.trialProgramIds}
        items={trialProgramsList}
        selectedKeys={filters.trialProgramIds ?? []}
        mode="multiple"
        onSelectedKeysChange={onTrialFilterChange}
      />
      <Tabs
        radius="full"
        aria-label="employee status filter"
        selectedKey={checkMaintenanceStatus()}
        onSelectionChange={onSelectionChangeStatus}
        classNames={{
          tabList: 'bg-base-50 p-0 gap-0',
          tabContent: 'px-2 text-s-sb group-data-[selected=true]:text-base-90',
          cursor: 'bg-base-5',
        }}
      >
        <Tab key={MaintenanceStatus.All} title="All" />
        <Tab key={MaintenanceStatus.Maintenance} title="Maintenance" />
        <Tab key={MaintenanceStatus.Operational} title="Operational" />
      </Tabs>
      {hasFilters && (
        <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="light" onClick={clearFilters}>
          Clear all
        </Button>
      )}
    </div>
  );
};
